import {gsap} from "gsap";
import {
    SimplePresentation,
    SimplePresentationRunnerController
} from "../../../../core/winpresentation/SimplePresentationRunner";
import {WinConfig, WinConfigUtil} from "../../../utils/WinConfigUtil";
import {BS2} from "../../../BS2";
import {BS2GameMode} from "../../../server/BS2GameMode";
import {BS2Statics} from "../../../config/BS2Statics";
import {BS2Sound} from "../../../config/sound/BS2Sound";
import {SymbolUtils} from "../../../utils/SymbolUtils";
import {BS2Symbol, BS2SymbolStates} from "../../../reels/BS2Symbol";
import {GSAPUtils} from "../../../utils/GSAPUtils";
import {SimpleReelModelSymbolData} from "../../../../core/reel/simplereel/SimpleReelModel";
import {BetWayWin, WinLineData} from "../../../server/BS2Data";
import {StateMatrixCoordinate} from "../../../utils/StateMatrix";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";


/**
 * Created by jonas on 2022-01-12.
 */
export interface IBetData {
    betWin: BetWayWin,
    reelPos: StateMatrixCoordinate
}

export class WinSummery implements SimplePresentation {

    name: string = "WinSummery";

    constructor() {
    }

    shouldRun(): boolean {
        return BS2.model.winData.winRatio > 0 && !BS2.model.gameData.brokeTheBank && !BS2.isWinBelowStakeSpinWinnings();
    }

    start(controller: SimplePresentationRunnerController): void {
        const data = this.calculateDataStuff(),
            isFastSpin: boolean = BS2.apiPlugin.settings.get(APISetting.FAST_SPIN),
            tl: gsap.core.Timeline = gsap.timeline({onComplete: () => controller.finish()}),
            isTiny: boolean = data.lastLevelConfig.name == "TINY" || data.lastLevelConfig.name == "SMALL",
            lineFlashing: gsap.core.Timeline | undefined = this.getLineFlashing(isFastSpin),
            winHighlight: gsap.core.Timeline = this.getWinHighlight(data.initSound, isTiny, isFastSpin);
        let noWinStartTime: number = 0;
        //Final assembly
        if (lineFlashing) {
            tl.add(lineFlashing, ">0.5");
            noWinStartTime = tl.duration() - 0.2;
            tl.add(winHighlight, ">-0.2");
        } else {
            tl.add(winHighlight);
        }


        if (!data.isBigWin) {
            if (data.hasMultipleGroupWins) {
                //Normal wins:
                const firstNormal = data.winConfigs[0];
                const lastNormal = data.winConfigs[data.winConfigs.length - 1];
                const normalWinTl = gsap.timeline();
                const skipCountSound = data.belowStakeWinRestriction || firstNormal.toRatio < 3; //below stake or < medium win.

                if (firstNormal.sound?.start && !skipCountSound) {
                    if ((BS2.isWinBelowStake() && !(BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_2 || BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_1)) || (!BS2.isWinBelowStake())) {
                        normalWinTl.add(() => BS2.soundPlayer.playSound(firstNormal.sound?.start!));
                    }
                }

                const countDuration = lastNormal.duration > 0 ? winHighlight.duration() : 0;

                normalWinTl.add(BS2.winView.getWinCountUp(
                    firstNormal.fromRatio * BS2.model.gameData.playedBetValue,
                    lastNormal.toRatio * BS2.model.gameData.playedBetValue,
                    countDuration,
                    0,
                    new PIXI.Point(360, 290),
                    skipCountSound
                ));

                if (lastNormal.duration > 0) {
                    tl.add(normalWinTl, "<");
                } else {
                    tl.add(normalWinTl, "<0.23");
                }
            }
            controller.setSkipCallback(() => tl.timeScale(2));
        } else {
            controller.setSkipCallback(() => tl.timeScale(2));
            const winSymbols: SimpleReelModelSymbolData[] = SymbolUtils.getAllSymbolsWithState(BS2SymbolStates.WIN_HIGHLIGHT);
            if ((BS2.isWinBelowStake() && !(BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_2 || BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_1)) || (!BS2.isWinBelowStake())) {
                tl.add(() => {
                    BS2.soundPlayer.playSound(BS2Sound.BIG_WIN_INTRO)
                }, ">0.1");
            }

            tl.add(SymbolUtils.getSymbolShakeAnimations(winSymbols, 0.77), "<")
            tl.add(gsap.to(this, {duration: 0.77}), "<"); //Adds delay so that bigwin starts 1.5 seconds after sound starts.

            let prevConf;
            while (data.winConfigs.length > 0) {
                const config: WinConfig = data.winConfigs.shift()!;

                if (prevConf && prevConf.name == config.name) {
                    delete config.sound;
                    delete config.anim;
                }
                if (prevConf && prevConf.multiplier == config.multiplier) {
                    config.newMultiplier = false;
                }

                const labelName: string = config.name + "_x" + config.multiplier;

                tl.add(() => controller.setSkipCallback(() => tl.seek(labelName, false)));
                tl.add(this.getBigWinLevel(config, BS2.model.gameData.playedBetValue, data.winConfigs.length == 0));
                tl.addLabel(labelName);
                prevConf = config;
            }
        }


        //Countup scale
        if (data.hasMultipleGroupWins || data.isBigWin) {
            tl.add(BS2.winView.getCountUpScaleAnimation(0, BS2.model.gameData.thisSpinWinning, tl.duration(), 0.4, data.lastLevelConfig.textScale + 0.1), 0);
        }

        const noWin = this.getNoWin();
        if (noWin) {
            tl.add(noWin, noWinStartTime);
        }

        tl.add(() => {
            controller.setSkipCallback();
        });

        const useTotalWin: boolean = BS2.model.gameData.accumulatedRoundWin != BS2.model.gameData.thisSpinWinning || BS2.model.data.mode != BS2GameMode.NORMAL;
        const stayDuration: number = data.isBigWin ? 2 : 0.5;
        //tl.add(() => BS2.keypadPlugin.setWin(BS2.model.gameData.accumulatedRoundWin, useTotalWin, BS2.isWinBelowStake()));


        const endSound = data.lastLevelConfig.sound?.end;
        if (endSound) {
            tl.add(() => BS2.soundPlayer.stopWinSound());
            if ((BS2.isWinBelowStake() && !(BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_2 || BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_1)) || (!BS2.isWinBelowStake())) {
                tl.add(() => BS2.soundPlayer.playSound(endSound));
            }
        }

        tl.add(() => {
            if ((BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_2) || (BS2.model.isRestore && BS2.model.data.enhancedBet && (BS2.model.data.mode === "SUPER_FREESPIN" || BS2.model.data.mode === "SUPER_FREESPIN_AVALANCHE"))) {
                BS2.soundPlayer.fadeInLoopMusic(BS2Sound.XBET2_LOOP, 1);
            } else {
                BS2.soundPlayer.fadeInMusic(1000);
            }
        });


        if (data.hasMultipleGroupWins || data.isBigWin) {
            tl.add(BS2.winView.getBigWinEnd(stayDuration));
            tl.add(() => {
                BS2.winView.getWinCountUpEnd(BS2.model.gameData.thisSpinWinning, stayDuration, BS2.model.winData.winWays)
            }, "<");

            const endAndMoveCountToCenterTl = gsap.timeline();
            endAndMoveCountToCenterTl.add(BS2.winView.getPositionAnimation(0.1, new PIXI.Point(360, 290), "power2.out"));
            endAndMoveCountToCenterTl.add(BS2.winView.getCountUpScaleAnimation(BS2.model.gameData.thisSpinWinning, BS2.model.gameData.thisSpinWinning, 0.1, data.lastLevelConfig.textScale, data.lastLevelConfig.textScale + 0.2), "<");
            endAndMoveCountToCenterTl.add(() => BS2.keypadPlugin.setWin(BS2.model.gameData.accumulatedRoundWin, true, BS2.isWinBelowStake()));

            tl.add(endAndMoveCountToCenterTl, "<-" + endAndMoveCountToCenterTl.duration);


            tl.add(() => {
                BS2.winView.getWinCountUpFade(BS2.model.gameData.thisSpinWinning, 0.5, 0.5);
            });

        }


    }

    private getAllConfigsForLevel(levelName: string, winConfigs: WinConfig[]) {
        const result = [];
        for (let conf of winConfigs) {
            if (conf.name == levelName) {
                result.push(conf);
            }
        }
        return result;
    }

    private calculateDataStuff(): any {
        const isBigWin: boolean = BS2.model.winData.winRatio >= 18,
            belowStakeWinRestriction: boolean = BS2.isWinBelowStakeSpinWinnings();

        const multiplier: number = 1;
        let winConfigs: WinConfig[] = [];
        for (let i: number = 1; i <= multiplier; i++) {
            const startRatio: number = BS2.model.winData.winRatio * (i - 1),
                endRatio: number = BS2.model.winData.winRatio * i;
            if (isBigWin) {
                winConfigs = winConfigs.concat(WinConfigUtil.getBigWinLevels(startRatio, endRatio, i, i === multiplier));
            } else {
                winConfigs = winConfigs.concat(WinConfigUtil.getNormalWinLevels(startRatio, endRatio, i, i === multiplier));
            }
        }


        const lastLevelName: string = winConfigs[winConfigs.length - 1].name;
        if (isBigWin) {
            //This makes sure that the last bigwin level is played in full duration but only to the win amount. With the multipliers spaced out based on the multiplier ratio.
            let lastLevelConfigs = this.getAllConfigsForLevel(lastLevelName, winConfigs);
            for (let config of lastLevelConfigs) {
                config.durationRatio = config.calculateDurationNew(lastLevelConfigs[0].fromRatio, lastLevelConfigs[lastLevelConfigs.length - 1].toRatio);
            }
        }

        const lastLevelConfig: WinConfig = winConfigs[winConfigs.length - 1].clone();
        let soundName: string = "";
        if (lastLevelConfig.sound?.init && BS2.model.winData.lines.length > 0) {
            soundName = lastLevelConfig.sound?.init!;
        }


        return {
            hasMultipleGroupWins: BS2.model.winData.lines.length > 1,
            isBigWin: isBigWin,
            initSound: soundName,
            winConfigs: winConfigs,
            belowStakeWinRestriction: belowStakeWinRestriction,
            lastLevelConfig: lastLevelConfig
        }
    }

    private getBigWinLevel(bigWinConfig: WinConfig, playedBetValue: number, lastLevel: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (bigWinConfig.newMultiplier && bigWinConfig.multiplier > 1) {
            tl.add(BS2.winView.getWinCountUpEnd(bigWinConfig.fromRatio * playedBetValue, 0.0));
        }

        const countUp: gsap.core.Timeline = BS2.winView.getWinCountUp(
            bigWinConfig.fromRatio * playedBetValue,
            bigWinConfig.toRatio * playedBetValue,
            bigWinConfig.duration * bigWinConfig.durationRatio,
            0,
            new PIXI.Point(360, 550),
            true,
            true
        );

        tl.add(countUp);

        if (bigWinConfig.sound && bigWinConfig.sound.start) {
            if ((BS2.isWinBelowStake() && !(BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_2 || BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_1)) || (!BS2.isWinBelowStake())) {
                tl.add(() => {
                    BS2.soundPlayer.playWinSound(bigWinConfig.sound!.start!);
                }, 0)
            }
        }

        if (bigWinConfig.isBigWin) {
            tl.add(() => {
                BS2.soundPlayer.fadeOutLoopMusic(BS2Sound.XBET2_LOOP, 300);
                BS2.soundPlayer.fadeOutMusic(300);
            }, 0)
        }

        if (bigWinConfig.anim) {
            tl.add(BS2.winView.getBigWinAnimation(bigWinConfig.anim!, bigWinConfig.particleLevel, bigWinConfig.duration * bigWinConfig.durationRatio), "<");
        }
        return tl;
    }

    private getLineFlashing(isFastSpin: boolean): gsap.core.Timeline | undefined {
        let lineFlashing: gsap.core.Timeline[] | undefined;
        if (BS2.model.winData.lines.length > 0) {
            lineFlashing = [];
            BS2Sound.WIN_LINE.reset();
            const winSymbolsByLine: SimpleReelModelSymbolData[][] = SymbolUtils.getAllWinningSymbolsByLine();
            const winLineAmountData: IBetData[] = this.getWinWayAmountData();
            for (let j: number = 0; j < winSymbolsByLine.length; j++) {
                const line: SimpleReelModelSymbolData[] = winSymbolsByLine[j];
                const lineData: IBetData = winLineAmountData[j];
                const lineTl: gsap.core.Timeline = gsap.timeline();
                if ((BS2.isWinBelowStake() && !(BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_2 || BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_1)) || (!BS2.isWinBelowStake())) {
                    lineTl.add(() => BS2.soundPlayer.playSound(BS2Sound.WIN_LINE.next()));
                }
                for (let i: number = 0; i < line.length; i++) {
                    const symbolData: SimpleReelModelSymbolData = line[i];
                    const symbol: BS2Symbol = <BS2Symbol>symbolData.positionable;
                    lineTl.add(symbol.getWinLineFlash(), 0);
                }
                lineTl.add(BS2.winView.playLineWinDisplayGroup(lineData), 0);
                if (j === winSymbolsByLine.length - 1) {
                    lineTl.add(() => {
                        BS2.winView.removeWinLine();
                    })
                }
                isFastSpin ? lineTl.timeScale(1.9) : lineTl.timeScale(1.3);
                lineFlashing.push(lineTl);
            }
        }
        if (lineFlashing) {
            return GSAPUtils.createTimelineFromArray(lineFlashing, "<0.45");
        }

        return undefined;
    }

    private getWinWayAmountData(): { betWin: BetWayWin, reelPos: StateMatrixCoordinate }[] {
        const winWayAmountData: { betWin: BetWayWin, reelPos: StateMatrixCoordinate }[] = [];
        for (let i: number = 0; i < BS2.model.winData.lines.length; i++) {
            const line: WinLineData = BS2.model.winData.lines[i];
            const winPositions: StateMatrixCoordinate[] = line.matrix.getAllCoordinatesForValue(true);
            let lastPos: StateMatrixCoordinate = winPositions.pop()!;

            winWayAmountData.push({
                betWin: line.betWayWin,
                reelPos: lastPos,
            });
        }
        return winWayAmountData;
    }

    private getNoWin(): gsap.core.Timeline | undefined {
        if (BS2.model.winData.lines.length > 0) {
            const noWinSymbols = SymbolUtils.getAllSymbolsWithState(BS2SymbolStates.NO_WIN);
            return GSAPUtils.createTimelineFromArray(SymbolUtils.getSymbolStateAnimations(noWinSymbols, BS2SymbolStates.NO_WIN), "<");
        }
        return undefined;
    }

    private getWinHighlight(winSound: string, isTiny: boolean, isFastSpin: boolean): gsap.core.Timeline {
        const winSymbols: SimpleReelModelSymbolData[][] = SymbolUtils.getAllSymbolsWithStateByReel(BS2SymbolStates.WIN_HIGHLIGHT),
            winSymbolsTl: gsap.core.Timeline = gsap.timeline(),
            delay: number = isTiny ? 0 : BS2Statics.WIN_SYMBOL_DELAY;

        for (let reel of winSymbols) {
            const reelTl: gsap.core.Timeline = gsap.timeline();
            for (let sym of reel) {
                sym.removed = true;
                reelTl.add((<BS2Symbol>sym.positionable).getStateAnimation(BS2SymbolStates.WIN_HIGHLIGHT), 0);
            }
            winSymbolsTl.add(reelTl, `<${delay}`);

        }

        if (winSound) {
            if ((BS2.isWinBelowStake() && !(BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_2 || BS2.apiPlugin.betFeatureController.getActiveBetFeature()?.name === BS2Statics.BOOSTED_BET_1)) || (!BS2.isWinBelowStake())) {
                winSymbolsTl.add(() => BS2.soundPlayer.playSound(winSound), 0);
            }
        }
        return winSymbolsTl;
    }


}